import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaCheckCircle, FaExclamationCircle, FaTimes } from 'react-icons/fa';

// Definimos los íconos para evitar problemas de tipos
const IconCheck = FaCheckCircle as any;
const IconError = FaExclamationCircle as any;
const IconClose = FaTimes as any;

// Definimos las propiedades que nuestro componente recibirá
interface NotificationProps {
  message: string;
  type: 'success' | 'error';
  onClose: () => void;
}

// Variantes de animación para la entrada y salida
const notificationVariants = {
  initial: { opacity: 0, y: 50, scale: 0.3 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: -20, scale: 0.8, transition: { duration: 0.2 } },
};

// Mapeo de estilos según el tipo de notificación - Glass morphism
const typeStyles = {
  success: {
    icon: <IconCheck className="text-green-400" />,
    bgColor: 'bg-green-500/20',
    borderColor: 'border-green-400/50',
    ringColor: 'ring-green-400/30',
  },
  error: {
    icon: <IconError className="text-red-400" />,
    bgColor: 'bg-red-500/20',
    borderColor: 'border-red-400/50',
    ringColor: 'ring-red-400/30',
  },
};

export const Notification = ({ message, type, onClose }: NotificationProps) => {
  // Cierra automáticamente la notificación después de 5 segundos
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 5000);

    return () => clearTimeout(timer);
  }, [onClose]);

  const styles = typeStyles[type];

  return (
    <motion.div
      variants={notificationVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      // Posición fija en la esquina inferior derecha, con padding para evitar navbar
      className={`fixed bottom-28 right-5 z-50 flex items-center p-4 max-w-sm w-full rounded-2xl shadow-2xl backdrop-blur-xl border-2 ring-2 ${styles.bgColor} ${styles.borderColor} ${styles.ringColor}`}
    >
      <div className="text-2xl flex-shrink-0">{styles.icon}</div>
      <div className="ml-3 flex-1">
        <p className="text-sm font-semibold text-white drop-shadow-md">{message}</p>
      </div>
      <button
        onClick={onClose}
        className="ml-auto -mx-1.5 -my-1.5 p-1.5 rounded-lg hover:bg-white/20 inline-flex h-8 w-8 items-center justify-center transition-colors"
      >
        <IconClose className="text-white/80 hover:text-white" />
      </button>
    </motion.div>
  );
};
